import React, { useState, useEffect, useRef, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { X, ChevronDown } from 'lucide-react';
import { API_PATH } from '../config';

const COMMON_CURRENCIES = [
  { value: 'USD', label: 'USD', symbol: '$' },
  { value: 'EUR', label: 'EUR', symbol: '€' },
  { value: 'GBP', label: 'GBP', symbol: '£' },
  { value: 'JPY', label: 'JPY', symbol: '¥' },
  { value: 'CNY', label: 'CNY', symbol: '¥' },
  { value: 'CHF', label: 'CHF', symbol: 'CHF' },
  { value: 'AUD', label: 'AUD', symbol: 'A$' },
  { value: 'CAD', label: 'CAD', symbol: 'C$' },
  { value: 'INR', label: 'INR', symbol: '₹' },
  { value: 'SGD', label: 'SGD', symbol: 'S$' },
  { value: 'HKD', label: 'HKD', symbol: 'HK$' },
  { value: 'NZD', label: 'NZD', symbol: 'NZ$' },
  { value: 'SEK', label: 'SEK', symbol: 'kr' },
  { value: 'NOK', label: 'NOK', symbol: 'kr' },
  { value: 'KRW', label: 'KRW', symbol: '₩' },
  { value: 'MXN', label: 'MXN', symbol: '$' },
  { value: 'BRL', label: 'BRL', symbol: 'R$' },
  { value: 'ARS', label: 'ARS', symbol: '$' },
  { value: 'CLP', label: 'CLP', symbol: '$' },
  { value: 'COP', label: 'COP', symbol: '$' },
  { value: 'PEN', label: 'PEN', symbol: 'S/' },
  { value: 'DKK', label: 'DKK', symbol: 'kr' },
  { value: 'PLN', label: 'PLN', symbol: 'zł' },
  { value: 'CZK', label: 'CZK', symbol: 'Kč' },
  { value: 'HUF', label: 'HUF', symbol: 'Ft' },
  { value: 'RON', label: 'RON', symbol: 'lei' },
  { value: 'TRY', label: 'TRY', symbol: '₺' },
  { value: 'THB', label: 'THB', symbol: '฿' },
  { value: 'MYR', label: 'MYR', symbol: 'RM' },
  { value: 'IDR', label: 'IDR', symbol: 'Rp' },
  { value: 'PHP', label: 'PHP', symbol: '₱' },
  { value: 'VND', label: 'VND', symbol: '₫' },
  { value: 'AED', label: 'AED', symbol: 'د.إ' },
  { value: 'SAR', label: 'SAR', symbol: '﷼' },
  { value: 'ILS', label: 'ILS', symbol: '₪' },
  { value: 'ZAR', label: 'ZAR', symbol: 'R' },
  { value: 'NGN', label: 'NGN', symbol: '₦' },
  { value: 'EGP', label: 'EGP', symbol: '£' },
  { value: 'MAD', label: 'MAD', symbol: 'د.م.' },
  { value: 'KES', label: 'KES', symbol: 'KSh' },
];

function CurrencySelect({ value, onChange }) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const ref = useRef(null);

  const filtered = COMMON_CURRENCIES.filter(c =>
    c.value.toLowerCase().includes(search.toLowerCase())
  );

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  return (
    <div className="tw-relative tw-flex-shrink-0" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className="tw-flex tw-items-center tw-gap-1 tw-px-2 tw-py-1 tw-rounded-lg tw-bg-white/5 tw-border tw-border-white/10 tw-text-sm tw-font-semibold tw-text-white tw-min-w-[64px] tw-justify-between tw-transition-colors hover:tw-bg-white/10"
      >
        <span>{value}</span>
        <ChevronDown size={12} />
      </button>
      {open && (
        <div className="tw-absolute tw-top-full tw-left-0 tw-mt-1 tw-z-50 tw-w-36 tw-rounded-xl tw-bg-[#1a1f2e] tw-border tw-border-white/10 tw-shadow-2xl tw-overflow-hidden">
          <div className="tw-p-1.5">
            <input
              autoFocus
              type="text"
              placeholder="Search..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="tw-w-full tw-bg-white/5 tw-border tw-border-white/10 tw-rounded-lg tw-px-2 tw-py-1 tw-text-xs tw-text-white tw-outline-none placeholder:tw-text-white/30"
            />
          </div>
          <div className="tw-max-h-48 tw-overflow-y-auto">
            {filtered.map(c => (
              <button
                key={c.value}
                type="button"
                onClick={() => { onChange(c.value); setOpen(false); setSearch(''); }}
                className={`tw-w-full tw-text-left tw-px-3 tw-py-1.5 tw-text-sm tw-transition-colors hover:tw-bg-white/10 ${value === c.value ? 'tw-text-accent-glow tw-font-semibold' : 'tw-text-white/80'}`}
              >
                {c.value}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// Simple inline calculator
function Calculator({ value, onChange, onClose }) {
  const [display, setDisplay] = useState(String(value || '0'));
  const [waitingForOperand, setWaitingForOperand] = useState(false);
  const [operator, setOperator] = useState(null);
  const [storedValue, setStoredValue] = useState(null);

  const handleDigit = (d) => {
    if (waitingForOperand) {
      setDisplay(d);
      setWaitingForOperand(false);
    } else {
      setDisplay(prev => prev === '0' ? d : prev + d);
    }
  };

  const handleDot = () => {
    if (waitingForOperand) { setDisplay('0.'); setWaitingForOperand(false); return; }
    if (!display.includes('.')) setDisplay(prev => prev + '.');
  };

  const handleOp = (op) => {
    const current = parseFloat(display);
    if (storedValue !== null && operator && !waitingForOperand) {
      const result = compute(storedValue, current, operator);
      setDisplay(String(result));
      setStoredValue(result);
      onChange(result);
    } else {
      setStoredValue(current);
    }
    setOperator(op);
    setWaitingForOperand(true);
  };

  const compute = (a, b, op) => {
    if (op === '+') return round(a + b);
    if (op === '-') return round(a - b);
    if (op === '×') return round(a * b);
    if (op === '÷') return b !== 0 ? round(a / b) : a;
    return b;
  };

  const round = (n) => Math.round(n * 1e8) / 1e8;

  const handleEquals = () => {
    const current = parseFloat(display);
    if (storedValue !== null && operator) {
      const result = compute(storedValue, current, operator);
      setDisplay(String(result));
      setStoredValue(null);
      setOperator(null);
      setWaitingForOperand(true);
      onChange(result);
    }
  };

  const handleClear = () => {
    setDisplay('0');
    setStoredValue(null);
    setOperator(null);
    setWaitingForOperand(false);
  };

  const handleBackspace = () => {
    if (waitingForOperand) return;
    setDisplay(prev => prev.length > 1 ? prev.slice(0, -1) : '0');
  };

  const btn = (label, onClick, extra = '') => (
    <button
      type="button"
      onClick={onClick}
      className={`tw-flex tw-items-center tw-justify-center tw-rounded-lg tw-h-9 tw-text-sm tw-font-medium tw-transition-colors ${extra}`}
    >
      {label}
    </button>
  );

  return (
    <div className="tw-bg-[#111827] tw-rounded-xl tw-border tw-border-white/10 tw-p-3 tw-mt-2">
      <div className="tw-text-right tw-text-lg tw-font-mono tw-text-white tw-bg-white/5 tw-rounded-lg tw-px-3 tw-py-2 tw-mb-2 tw-min-h-[2.5rem] tw-break-all">
        {display}
      </div>
      <div className="tw-grid tw-grid-cols-4 tw-gap-1.5">
        {btn('C', handleClear, 'tw-bg-red-500/20 tw-text-red-400 hover:tw-bg-red-500/30')}
        {btn('⌫', handleBackspace, 'tw-bg-white/5 tw-text-white/70 hover:tw-bg-white/10')}
        {btn('%', () => { const v = round(parseFloat(display) / 100); setDisplay(String(v)); onChange(v); }, 'tw-bg-white/5 tw-text-white/70 hover:tw-bg-white/10')}
        {btn('÷', () => handleOp('÷'), operator === '÷' ? 'tw-bg-accent/40 tw-text-accent-glow' : 'tw-bg-accent/20 tw-text-accent-glow hover:tw-bg-accent/30')}

        {['7','8','9'].map(d => btn(d, () => handleDigit(d), 'tw-bg-white/5 tw-text-white hover:tw-bg-white/10'))}
        {btn('×', () => handleOp('×'), operator === '×' ? 'tw-bg-accent/40 tw-text-accent-glow' : 'tw-bg-accent/20 tw-text-accent-glow hover:tw-bg-accent/30')}

        {['4','5','6'].map(d => btn(d, () => handleDigit(d), 'tw-bg-white/5 tw-text-white hover:tw-bg-white/10'))}
        {btn('-', () => handleOp('-'), operator === '-' ? 'tw-bg-accent/40 tw-text-accent-glow' : 'tw-bg-accent/20 tw-text-accent-glow hover:tw-bg-accent/30')}

        {['1','2','3'].map(d => btn(d, () => handleDigit(d), 'tw-bg-white/5 tw-text-white hover:tw-bg-white/10'))}
        {btn('+', () => handleOp('+'), operator === '+' ? 'tw-bg-accent/40 tw-text-accent-glow' : 'tw-bg-accent/20 tw-text-accent-glow hover:tw-bg-accent/30')}

        {btn('0', () => handleDigit('0'), 'tw-col-span-2 tw-bg-white/5 tw-text-white hover:tw-bg-white/10')}
        {btn('.', handleDot, 'tw-bg-white/5 tw-text-white hover:tw-bg-white/10')}
        {btn('=', handleEquals, 'tw-bg-accent tw-text-white hover:tw-bg-accent/80')}
      </div>
      <button
        type="button"
        onClick={onClose}
        className="tw-w-full tw-mt-2 tw-py-1.5 tw-rounded-lg tw-text-xs tw-text-white/50 hover:tw-text-white/80 hover:tw-bg-white/5 tw-transition-colors"
      >
        Close calculator
      </button>
    </div>
  );
}

const STORAGE_KEY = (tripId) => `currency_converter_${tripId}`;

export default function CurrencyConverterModal({ open, onClose, tripId, user }) {
  const userCurrency = user?.currency || 'EUR';

  const getDefaultCurrencies = () => {
    const defaults = ['USD', userCurrency, 'GBP', 'JPY'];
    // Ensure no duplicates
    const seen = new Set();
    const fallbacks = ['EUR', 'GBP', 'JPY', 'AUD', 'CAD', 'CHF'];
    const result = [];
    for (const c of defaults) {
      if (!seen.has(c)) { seen.add(c); result.push(c); }
    }
    for (const c of fallbacks) {
      if (result.length >= 4) break;
      if (!seen.has(c)) { seen.add(c); result.push(c); }
    }
    return result.slice(0, 4);
  };

  const loadSaved = () => {
    if (!tripId) return getDefaultCurrencies();
    try {
      const saved = localStorage.getItem(STORAGE_KEY(tripId));
      if (saved) return JSON.parse(saved);
    } catch {}
    return getDefaultCurrencies();
  };

  const [rates, setRates] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currencies, setCurrencies] = useState(loadSaved);
  const [values, setValues] = useState(['1', '', '', '']);
  const [activeCalc, setActiveCalc] = useState(null);

  // Reload saved currencies when tripId changes or modal opens
  useEffect(() => {
    if (!open) return;
    setCurrencies(loadSaved());
  }, [open, tripId]);

  const saveCurrencies = (newCurrencies) => {
    if (!tripId) return;
    try { localStorage.setItem(STORAGE_KEY(tripId), JSON.stringify(newCurrencies)); } catch {}
  };

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    fetch(`${API_PATH}/exchange_rates.php`)
      .then(r => r.json())
      .then(data => {
        if (data.result === 'success') setRates(data.rates);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [open]);

  // Convert from one currency to another via USD as base — returns plain numeric string (no commas)
  const convert = useCallback((amount, from, to) => {
    if (!rates || isNaN(amount)) return '';
    const fromRate = rates[from] || 1;
    const toRate = rates[to] || 1;
    const usdAmount = amount / fromRate;
    const result = usdAmount * toRate;
    const abs = Math.abs(result);
    const decimals = abs >= 1000 ? 2 : abs >= 1 ? 4 : 6;
    return parseFloat(result.toFixed(decimals)).toString();
  }, [rates]);

  const handleValueChange = (index, rawVal) => {
    const num = parseFloat(rawVal);
    const newValues = [...values];
    newValues[index] = rawVal;
    if (!isNaN(num)) {
      for (let i = 0; i < 4; i++) {
        if (i !== index) {
          newValues[i] = convert(num, currencies[index], currencies[i]);
        }
      }
    }
    setValues(newValues);
  };

  const handleCurrencyChange = (index, newCurrency) => {
    const newCurrencies = [...currencies];
    newCurrencies[index] = newCurrency;
    setCurrencies(newCurrencies);
    saveCurrencies(newCurrencies);
    // Re-derive from first non-empty field
    const sourceIndex = values.findIndex(v => v !== '' && !isNaN(parseFloat(v)));
    if (sourceIndex >= 0) {
      const num = parseFloat(values[sourceIndex]);
      const newValues = [...values];
      for (let i = 0; i < 4; i++) {
        if (i !== sourceIndex) {
          newValues[i] = convert(num, newCurrencies[sourceIndex], newCurrencies[i]);
        }
      }
      setValues(newValues);
    }
  };

  const handleCalcResult = (index, result) => {
    handleValueChange(index, String(result));
  };

  if (!open) return null;

  return ReactDOM.createPortal(
    <div className="tw-fixed tw-inset-0 tw-z-50 tw-flex tw-items-center tw-justify-center tw-p-4">
      <div
        className="tw-absolute tw-inset-0 tw-bg-black/60 tw-backdrop-blur-sm"
        onClick={onClose}
      />
      <div className="tw-relative tw-w-full tw-max-w-sm tw-rounded-2xl tw-bg-[#151b2b] tw-border tw-border-white/10 tw-shadow-2xl tw-overflow-hidden">
        {/* Header */}
        <div className="tw-flex tw-items-center tw-justify-between tw-px-5 tw-py-4 tw-border-b tw-border-white/10">
          <h2 className="tw-text-base tw-font-bold tw-text-white">Currency Converter</h2>
          <button
            type="button"
            onClick={onClose}
            className="tw-p-1.5 tw-rounded-lg tw-text-white/50 hover:tw-text-white hover:tw-bg-white/10 tw-transition-colors"
          >
            <X size={16} />
          </button>
        </div>

        <div className="tw-px-5 tw-py-4 tw-space-y-3">
          {loading ? (
            <div className="tw-text-center tw-text-white/50 tw-py-6 tw-text-sm">Loading rates...</div>
          ) : !rates ? (
            <div className="tw-text-center tw-text-red-400 tw-py-6 tw-text-sm">Failed to load exchange rates</div>
          ) : (
            <>
              {[0, 1, 2, 3].map(i => (
                <div key={i}>
                  <div className="tw-flex tw-items-center tw-gap-2">
                    <CurrencySelect
                      value={currencies[i]}
                      onChange={(c) => handleCurrencyChange(i, c)}
                    />
                    <input
                      type="number"
                      value={values[i]}
                      onChange={e => handleValueChange(i, e.target.value)}
                      onFocus={() => setActiveCalc(null)}
                      placeholder="0"
                      className="tw-flex-1 tw-bg-white/5 tw-border tw-border-white/10 tw-rounded-lg tw-px-3 tw-py-2 tw-text-white tw-text-sm tw-outline-none focus:tw-border-accent/50 tw-transition-colors tw-min-w-0"
                    />
                    <button
                      type="button"
                      onClick={() => setActiveCalc(activeCalc === i ? null : i)}
                      className={`tw-p-2 tw-rounded-lg tw-transition-colors tw-flex-shrink-0 ${activeCalc === i ? 'tw-bg-accent/30 tw-text-accent-glow' : 'tw-text-white/40 hover:tw-text-white/80 hover:tw-bg-white/10'}`}
                      aria-label="Open calculator"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <rect width="16" height="20" x="4" y="2" rx="2"/>
                        <line x1="8" x2="16" y1="6" y2="6"/>
                        <line x1="8" x2="8.01" y1="10" y2="10"/>
                        <line x1="12" x2="12.01" y1="10" y2="10"/>
                        <line x1="16" x2="16.01" y1="10" y2="10"/>
                        <line x1="8" x2="8.01" y1="14" y2="14"/>
                        <line x1="12" x2="12.01" y1="14" y2="14"/>
                        <line x1="16" x2="16.01" y1="14" y2="14"/>
                        <line x1="8" x2="8.01" y1="18" y2="18"/>
                        <line x1="12" x2="12.01" y1="18" y2="18"/>
                        <line x1="16" x2="16.01" y1="18" y2="18"/>
                      </svg>
                    </button>
                  </div>
                  {activeCalc === i && (
                    <Calculator
                      value={parseFloat(values[i]) || 0}
                      onChange={(result) => handleCalcResult(i, result)}
                      onClose={() => setActiveCalc(null)}
                    />
                  )}
                </div>
              ))}

              <p className="tw-text-xs tw-text-white/30 tw-text-center tw-pt-1">
                Rates via USD · Updates daily
              </p>
            </>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
}
